import { CUSTOM_SERVICE_LAYER_SERVICES, getCustomServiceLayerBaseUrl } from 'services/constants';
import Service from './service';
import { getWmsServiceUrl } from '../../setup';
import { APIVersion, ServiceTypes } from '../../types/main';
import { getQueryStringByObject } from '../../utils/queryUtils';

class PortfolioManagement extends Service {
    static #baseCustomContactServiceUrl = getCustomServiceLayerBaseUrl(CUSTOM_SERVICE_LAYER_SERVICES.CONTACT);

    constructor() {
        super();
        this.user = null;
    }

    static getInvestmentOverview(contactId, params, version = APIVersion.V10) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl(version)}/contacts/${contactId}/investment-overview?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN, axiosSetup: { headers: { 'accept-language': params?.language } } },
        );
    }

    static getJointPortfolios(contactId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${this.#baseCustomContactServiceUrl}/${contactId}/portfolios/my-accounts?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN, axiosSetup: { headers: { 'accept-language': params?.language } } },
        );
    }

    static getPOAPortfolios(contactId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${this.#baseCustomContactServiceUrl}/${contactId}/portfolios/power-of-attorney?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN, axiosSetup: { headers: { 'accept-language': params?.language } } },
        );
    }

    static getPAPortfolios(contactId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${this.#baseCustomContactServiceUrl}/${contactId}/portfolios/parental-authority?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN, axiosSetup: { headers: { 'accept-language': params?.language } } },
        );
    }

    static getHistoricalPerformance(contactId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/investment-overview/performance?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getPortfolios({ contactId, ...params }) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getPortfolioDetails(portfolioId, contactId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getPortfolioOverview(portfolioId, contactId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}/overview?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getInvestmentOverviewPerformance(contactId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/investment-overview/performance?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getPortfolioDetailsPerformance(
        portfolioId,
        contactId,
        params,
        requestOptions,
    ) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}/performance?${queryString}`,
            {
                ...(requestOptions || {}),
                isSecure: true,
                type: ServiceTypes.WITH_MEMBER_TOKEN,
            },
        );
    }

    static getModelPortfolio(portfolioId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl(APIVersion.V12)}/model-portfolios/${portfolioId}?${queryString}`,
            { isSecure: true, cache: false, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getModelPortfolios(productId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl()}/products/${productId}/model-portfolios?${queryString}`,
            { isSecure: true, cache: false, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getModelPortfolioDetails(memberId, modelPortfolioId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl(APIVersion.V12)}/members/${memberId}/model-portfolios/${modelPortfolioId}?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getPortfolioTrnsactions(contactId, portfolioId, filter) {
        return Service.postRequest(
            `${getWmsServiceUrl(APIVersion.V11)}/contacts/${contactId}/portfolios/${portfolioId}/transactions`,
            filter,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getPortfolioAccounts(contactId, portfolioId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}/accounts?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static postPortfolioOrder(contactId, portfolioId, options) {
        return Service.postRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}/order`,
            options,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getPortfolioOrderBook(contactId, portfolioId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}/orderbook?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static postPortfolioOrderBook(contactId, portfolioId, options) {
        return Service.postRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}/orderbook`,
            options,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getPortfolioChanges(contactId, portfolioId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl(APIVersion.V12)}/contacts/${contactId}/portfolios/${portfolioId}/changes?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getPortfolioProposals(contactId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolioproposals?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static postPortfolioProposal(contactId, language, options, params) {
        const queryString = getQueryStringByObject(params);

        return Service.postRequest(
            `${getWmsServiceUrl(APIVersion.V12)}/contacts/${contactId}/portfolioproposals?language=${language}${queryString && `&${queryString}`}`,
            options,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getPortfolioProposal(contactId, portfolioProposalId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl(APIVersion.V11)}/contacts/${contactId}/portfolioproposals/${portfolioProposalId}?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static patchTradingCard(contactId, portfolioId, options) {
        return Service.patchRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}/tradingcart`,
            options,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static postPortfolioOrderbookSimulate(contactId, params, options) {
        const queryString = getQueryStringByObject(params);

        return Service.postRequest(
            `${getWmsServiceUrl(APIVersion.V11)}/contacts/${contactId}/portfolios/orderbook/simulate?${queryString}`,
            options,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static postPortfolioRebalance(contactId, portfolioId, options) {
        return Service.postRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}/rebalance`,
            options,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static postTradingCartOrder(contactId, portfolioId) {
        return Service.postRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}/tradingcart/order`,
            {},
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static postTradingCartTarget(contactId, portfolioId) {
        return Service.postRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}/tradingcart/target`,
            {},
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static deleteTradingCartOrder(contactId, portfolioId) {
        return Service.deleteRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}/tradingcart`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static sellPortfolio(contactId, portfolioId, options) {
        const queryString = getQueryStringByObject(options);

        return Service.postRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}/sell?${queryString}`,
            {},
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static tradingCardSimulate(contactId, portfolioId, params, options) {
        const queryString = getQueryStringByObject(params);

        return Service.postRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}/tradingcart/order/simulate?${queryString}`,
            options,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getPortfolioPositions(portfolioId, contactId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}/overview?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getTradingCard(portfolioId, contactId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}/tradingcart?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static createPortfolio(contactId, params, options) {
        const queryString = getQueryStringByObject(params);

        return Service.postRequest(
            `${getWmsServiceUrl(APIVersion.V11)}/contacts/${contactId}/portfolios?${queryString}`,
            options,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getPortfolioSecurityPerformance(
        portfolioId,
        contactId,
        securityId,
        params,
    ) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}/securities/${securityId}/performance?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getPortfolioSecurityMonetaryPerformance(
        portfolioId,
        contactId,
        securityId,
        params,
    ) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}/securities/${securityId}/monetary-performance?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getPortfolioPerformanceAttribution(portfolioId, contactId, options) {
        return Service.postRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}/performance-attribution-cumulative`,
            options,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getPortfolioAllocation(contactId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/allocations?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getContactsPortfolioAllocation(contactId, portfolioId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolios/${portfolioId}/allocations?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getModelPortfolioAllocation(modelPortfolioId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl()}/model-portfolios/${modelPortfolioId}/allocations?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }

    static getPortfolioProposalsAllocations(contactId, portfolioProposalId, params) {
        const queryString = getQueryStringByObject(params);

        return Service.getRequest(
            `${getWmsServiceUrl()}/contacts/${contactId}/portfolioproposals/${portfolioProposalId}/allocations?${queryString}`,
            { isSecure: true, type: ServiceTypes.WITH_MEMBER_TOKEN },
        );
    }
}

export default PortfolioManagement;
